import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { AGGREGATE_OPS, type AggregateOp, type AggregateSpec, type SortSpec, type TransformSpec } from '../../lib/mappingEngine';

const inputCls =
  'w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] font-mono outline-none focus:border-blue-500';
const labelCls = 'text-[11px] font-medium text-black/45 dark:text-white/45';

const splitList = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const asSort = (k: string | SortSpec): SortSpec => (typeof k === 'string' ? { field: k, order: 'asc' } : { field: k.field, order: k.order ?? 'asc' });

// "Shape the records" - spec.transform (group by / aggregate / sort by / limit
// / select fields), applied to the whole record list before the Signal rules
// run, the same for CSV, JSON, text and host-metric sources. Field names are
// source paths (dot-paths), suggested from the pasted sample.
export function TransformEditor({
  transform,
  paths,
  onChange,
}: {
  transform: TransformSpec;
  paths: string[];
  onChange: (t: TransformSpec) => void;
}) {
  const groupBy = transform.groupBy ?? [];
  const select = transform.select ?? [];
  // Aggregate / sort rows are held locally so a half-built row (no field yet)
  // can exist in the UI; the spec (setTransform) only ever receives complete ones.
  const [aggregate, setAggregate] = useState<AggregateSpec[]>(transform.aggregate ?? []);
  const [sortBy, setSortBy] = useState<SortSpec[]>((transform.sortBy ?? []).map(asSort));
  // Comma lists are typed into local text (a trailing comma must survive a
  // re-render) and committed on every change.
  const [groupText, setGroupText] = useState(groupBy.join(', '));
  const [selectText, setSelectText] = useState(select.join(', '));

  const patch = (p: Partial<TransformSpec>) => onChange({ ...transform, ...p });
  const commitAggs = (next: AggregateSpec[]) => {
    setAggregate(next);
    patch({ aggregate: next });
  };
  const commitSorts = (next: SortSpec[]) => {
    setSortBy(next);
    patch({ sortBy: next });
  };
  const setAgg = (i: number, p: Partial<AggregateSpec>) => commitAggs(aggregate.map((a, idx) => (idx === i ? { ...a, ...p } : a)));
  const setSort = (i: number, p: Partial<SortSpec>) => commitSorts(sortBy.map((k, idx) => (idx === i ? { ...k, ...p } : k)));

  const active = groupBy.length + aggregate.length + sortBy.length + select.length + (transform.limit !== undefined ? 1 : 0);

  return (
    <details open={active > 0} data-testid="transform-editor">
      <summary className="text-[11px] font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 cursor-pointer select-none">
        Shape records{active > 0 ? ` · ${active} step${active === 1 ? '' : 's'}` : ''}
      </summary>
      <div className="flex flex-col gap-2.5 mt-2">
        <datalist id="transform-paths">
          {paths.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        <p className="text-[11px] text-black/40 dark:text-white/40 m-0">
          Runs on the whole list before the fields below: group → sort → limit → select.
        </p>

        <label className="flex flex-col gap-1">
          <span className={labelCls}>Group by (comma separated)</span>
          <input
            aria-label="Group by"
            list="transform-paths"
            className={inputCls}
            value={groupText}
            placeholder="labels.mount, host"
            onChange={(e) => {
              setGroupText(e.target.value);
              patch({ groupBy: splitList(e.target.value) });
            }}
          />
        </label>

        <div className="flex flex-col gap-1.5">
          <span className={labelCls}>Aggregate</span>
          {aggregate.map((a, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <select aria-label={`Aggregate ${i + 1} operation`} className={`${inputCls} !w-24`} value={a.op} onChange={(e) => setAgg(i, { op: e.target.value as AggregateOp })}>
                {AGGREGATE_OPS.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
              <input
                aria-label={`Aggregate ${i + 1} field`}
                list="transform-paths"
                className={inputCls}
                value={a.field ?? ''}
                placeholder={a.op === 'count' ? '(all rows)' : 'field'}
                onChange={(e) => setAgg(i, { field: e.target.value })}
              />
              <span className="text-[11px] text-black/40 dark:text-white/40">as</span>
              <input aria-label={`Aggregate ${i + 1} name`} className={inputCls} value={a.as ?? ''} placeholder={a.field || 'count'} onChange={(e) => setAgg(i, { as: e.target.value })} />
              <button aria-label={`Remove aggregate ${i + 1}`} onClick={() => commitAggs(aggregate.filter((_, idx) => idx !== i))}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div>
            <Button variant="bordered" onClick={() => commitAggs([...aggregate, { op: 'avg', field: '' }])}>
              <Plus size={14} className="inline" /> Add aggregate
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className={labelCls}>Sort by</span>
          {sortBy.map((k, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input aria-label={`Sort ${i + 1} field`} list="transform-paths" className={inputCls} value={k.field} placeholder="field" onChange={(e) => setSort(i, { field: e.target.value })} />
              <select aria-label={`Sort ${i + 1} order`} className={`${inputCls} !w-24`} value={k.order} onChange={(e) => setSort(i, { order: e.target.value as 'asc' | 'desc' })}>
                <option value="asc">asc</option>
                <option value="desc">desc</option>
              </select>
              <button aria-label={`Remove sort ${i + 1}`} onClick={() => commitSorts(sortBy.filter((_, idx) => idx !== i))}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div>
            <Button variant="bordered" onClick={() => commitSorts([...sortBy, { field: '', order: 'asc' }])}>
              <Plus size={14} className="inline" /> Add sort
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-[6rem_1fr] gap-2">
          <label className="flex flex-col gap-1">
            <span className={labelCls}>Limit</span>
            <input
              aria-label="Limit"
              type="number"
              min={0}
              className={inputCls}
              value={transform.limit ?? ''}
              placeholder="all"
              onChange={(e) => patch({ limit: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelCls}>Select fields (comma separated, empty = all)</span>
            <input
              aria-label="Select fields"
              list="transform-paths"
              className={inputCls}
              value={selectText}
              placeholder="name, value, labels.mount"
              onChange={(e) => {
                setSelectText(e.target.value);
                patch({ select: splitList(e.target.value) });
              }}
            />
          </label>
        </div>
      </div>
    </details>
  );
}
